import { useState } from 'react';
import { apiFetch } from '../utils/api';
import { setStoredAuth } from './authUtils';
import { useTheme } from '../styles/ThemeContext';
import loginBg from '../assets/login-bg.webp';

export function LoginPage({ onSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'forgot-sent' | 'verify-ip'
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [verifyUserId, setVerifyUserId] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const { theme, mode, toggleMode } = useTheme();
  const c = theme.colors;

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password) {
      setError('Please enter username and password.');
      return;
    }

    setLoading(true);

    try {
      const res = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password })
      });

      const data = await res.json();

      if (res.status === 202 && data.requireIpVerification) {
        setVerifyUserId(data.userId);
        setVerifyCode('');
        setVerifyError('');
        setView('verify-ip');
        setLoading(false);
        return;
      }

      if (!res.ok) {
        setError(data.error || 'Login failed');
        setLoading(false);
        return;
      }

      setStoredAuth(data.token, data.user);
      onSuccess(data.token, data.user);
    } catch (err) {
      setError('Connection failed. Check if the server is running.');
      setLoading(false);
    }
  };

  const submitVerifyIp = async (e) => {
    e.preventDefault();
    setVerifyError('');
    if (verifyCode.length !== 6) { setVerifyError('Please enter the 6-digit code.'); return; }
    setVerifyLoading(true);
    try {
      const res = await apiFetch('/api/auth/verify-ip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: verifyUserId, code: verifyCode.trim() })
      });
      const data = await res.json();
      if (!res.ok) { setVerifyError(data.error || 'Verification failed'); return; }
      setStoredAuth(data.token, data.user);
      onSuccess(data.token, data.user);
    } catch {
      setVerifyError('Connection failed. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotUsername.trim()) { setForgotError('Please enter your username.'); return; }
    setForgotLoading(true);
    try {
      const res = await apiFetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotUsername.trim() })
      });
      const data = await res.json();
      if (!res.ok) { setForgotError(data.error || 'Request failed'); return; }
      setView('forgot-sent');
    } catch {
      setForgotError('Connection failed. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const iconUser = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );

  const iconLock = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );

  const overlayColor = mode === 'dark'
    ? 'rgba(0, 0, 0, 0.75)'
    : 'rgba(15, 23, 42, 0.50)';

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundImage: `url(${loginBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: c.bg,
        color: c.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Segoe UI', sans-serif",
        position: 'relative'
      }}
    >
      {/* Copyright */}
      <div style={{
        position: 'absolute',
        bottom: 16,
        width: '100%',
        textAlign: 'center',
        fontSize: 12,
        color: 'rgba(255,255,255,0.7)',
        zIndex: 1,
        pointerEvents: 'none'
      }}>
        &copy; MaicoSoft 2026
      </div>

      {/* 50% transparante overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: overlayColor,
        pointerEvents: 'none'
      }} />

      {/* Theme toggle in top-right corner */}
      <button
        onClick={toggleMode}
        title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1,
          ...theme.btnG,
          padding: '6px 12px',
          fontSize: 13
        }}
      >
        {mode === 'dark' ? '☀️' : '🌙'}
      </button>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          background: c.bgCard,
          border: '1px solid ' + c.border,
          padding: '48px 40px 40px',
          borderRadius: 16,
          width: 400,
          boxShadow: '0 12px 40px ' + c.shadow,
          backdropFilter: 'blur(2px)'
        }}
      >
        {/* Logo + Title */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div
            style={{
              background: c.accent,
              width: 80,
              height: 80,
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
              color: '#fff',
              borderRadius: 16,
              fontWeight: 800,
              letterSpacing: 1
            }}
          >
            TMS
          </div>
          <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 6 }}>Packing List Generator</div>
          <div style={{ fontSize: 13, color: c.accent, fontWeight: 500 }}>
            {view === 'login' ? 'Log in to continue' : view === 'forgot' ? 'Reset your password' : view === 'verify-ip' ? 'Verify your identity' : 'Request received'}
          </div>
        </div>

        {/* ── LOGIN FORM ── */}
        {view === 'login' && (
          <form onSubmit={submit} autoComplete="off">
            {/* Hidden dummy fields to absorb browser autofill */}
            <input type="text" name="prevent_autofill_u" style={{ display: 'none' }} tabIndex={-1} />
            <input type="password" name="prevent_autofill_p" style={{ display: 'none' }} tabIndex={-1} />

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: c.text, display: 'block', marginBottom: 6 }}>Username</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  name="tms_user_field"
                  value={username}
                  placeholder="Enter your username"
                  onChange={(e) => { setUsername(e.target.value); setError(''); }}
                  autoComplete="one-time-code"
                  autoFocus
                  style={{ ...theme.inp, padding: '12px 44px 12px 14px', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' }}
                />
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex' }}>{iconUser}</span>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: c.text, display: 'block', marginBottom: 6 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="password"
                  name="tms_pass_field"
                  value={password}
                  placeholder="Enter your password"
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  autoComplete="new-password"
                  style={{ ...theme.inp, padding: '12px 44px 12px 14px', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' }}
                />
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex' }}>{iconLock}</span>
              </div>
            </div>

            {error && <div style={{ color: c.error, fontSize: 12, marginBottom: 12 }}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{ ...theme.btnP, width: '100%', padding: '14px', borderRadius: 8, fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1 }}
            >
              {loading ? 'Loading…' : 'Log In'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => { setView('forgot'); setForgotError(''); setForgotUsername(''); }}
                style={{ background: 'none', border: 'none', color: c.accent, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
              >
                Forgot password?
              </button>
            </div>
          </form>
        )}

        {/* ── IP VERIFICATION FORM ── */}
        {view === 'verify-ip' && (
          <form onSubmit={submitVerifyIp} autoComplete="off">
            <div style={{ fontSize: 13, color: c.textSecondary, marginBottom: 20, lineHeight: 1.6 }}>
              A login was detected from a <strong style={{ color: c.text }}>new location</strong>. We sent a 6-digit verification code to your email address. Enter it below to continue.
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: c.text, display: 'block', marginBottom: 6 }}>Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={verifyCode}
                placeholder="Enter 6-digit code"
                onChange={(e) => { setVerifyCode(e.target.value.replace(/\D/g, '')); setVerifyError(''); }}
                autoFocus
                style={{ ...theme.inp, padding: '12px 14px', borderRadius: 8, fontSize: 22, letterSpacing: 10, textAlign: 'center', width: '100%', boxSizing: 'border-box', fontFamily: 'monospace' }}
              />
            </div>
            {verifyError && <div style={{ color: c.error, fontSize: 12, marginBottom: 12 }}>{verifyError}</div>}
            <button
              type="submit"
              disabled={verifyLoading || verifyCode.length !== 6}
              style={{ ...theme.btnP, width: '100%', padding: '14px', borderRadius: 8, fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', cursor: (verifyLoading || verifyCode.length !== 6) ? 'not-allowed' : 'pointer', opacity: (verifyLoading || verifyCode.length !== 6) ? 0.6 : 1 }}
            >
              {verifyLoading ? 'Verifying…' : 'Confirm'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => { setView('login'); setVerifyCode(''); setVerifyError(''); }}
                style={{ background: 'none', border: 'none', color: c.textSecondary, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
              >
                Back to login
              </button>
            </div>
          </form>
        )}

        {/* ── FORGOT PASSWORD FORM ── */}
        {view === 'forgot' && (
          <form onSubmit={submitForgot} autoComplete="off">
            <div style={{ fontSize: 13, color: c.textSecondary, marginBottom: 20, lineHeight: 1.6 }}>
              Enter your username and we will notify the administrator to reset your password.
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: c.text, display: 'block', marginBottom: 6 }}>Username</label>
              <input
                type="text"
                value={forgotUsername}
                placeholder="Enter your username"
                onChange={(e) => { setForgotUsername(e.target.value); setForgotError(''); }}
                autoFocus
                style={{ ...theme.inp, padding: '12px 14px', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            {forgotError && <div style={{ color: c.error, fontSize: 12, marginBottom: 12 }}>{forgotError}</div>}
            <button
              type="submit"
              disabled={forgotLoading || !forgotUsername.trim()}
              style={{ ...theme.btnP, width: '100%', padding: '14px', borderRadius: 8, fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', cursor: (forgotLoading || !forgotUsername.trim()) ? 'not-allowed' : 'pointer', opacity: (forgotLoading || !forgotUsername.trim()) ? 0.6 : 1 }}
            >
              {forgotLoading ? 'Sending…' : 'Send request'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setView('login')}
                style={{ background: 'none', border: 'none', color: c.textSecondary, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
              >
                Back to login
              </button>
            </div>
          </form>
        )}

        {/* ── REQUEST SENT CONFIRMATION ── */}
        {view === 'forgot-sent' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
            <div style={{ fontWeight: 600, fontSize: 15, color: c.text, marginBottom: 10 }}>Request received</div>
            <div style={{ fontSize: 13, color: c.textSecondary, lineHeight: 1.7, marginBottom: 24 }}>
              Your password reset request is being processed. The administrator will contact you shortly.
            </div>
            <button
              type="button"
              onClick={() => setView('login')}
              style={{ ...theme.btnP, width: '100%', padding: '14px', borderRadius: 8, fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer' }}
            >
              Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
